import { CountryCode } from '@/constants/countryCodes';
import type { Customer } from '@/types/checkout';
import type { AddressDetails } from '@/types/context';
import { isCountryCodeUSA } from './tax';

/**
 * Returns the ZIP the checkout currently holds, preferring the buyer-modified
 * value in `AddressDetails` over the session baseline in `Customer.address`.
 * Falls back to `''` when neither is set so callers can `if (zip)`-gate on the
 * presence of a real value (both underlying fields are typed `string`, but may
 * be empty).
 */
export function getCurrentCheckoutZipCode(
  addressDetails: AddressDetails | null | undefined,
  customer: Customer | null | undefined
): string {
  return addressDetails?.zipCode ?? customer?.address?.zipCode ?? '';
}

function validateUSZip(value: string): boolean {
  if (!value) {
    return false;
  }
  return /^\d{5}(-\d{4})?$/.test(value);
}

/**
 * Strips all whitespace and uppercases a ZIP / postal code so two values can be
 * compared for equality regardless of case or spacing (e.g. `"m5v 3l9"` and
 * `"M5V3L9"` both canonicalize to `"M5V3L9"`). Purely a comparison helper — for
 * user-facing formatting use `formatCanadianPostalCode` etc. instead.
 */
export function canonicalizeZipCode(value: string): string {
  return value.replace(/\s+/g, '').toUpperCase();
}

/**
 * Formats a Canadian postal code by adding a space after the first 3 characters
 * @param value - The postal code value (with or without space)
 * @returns Formatted postal code with space (e.g., "A1A 1A1")
 */
export function formatCanadianPostalCode(value: string): string {
  if (!value) return value;

  const cleaned = canonicalizeZipCode(value);

  if (cleaned.length >= 3) {
    return cleaned.slice(0, 3) + ' ' + cleaned.slice(3);
  }

  return cleaned;
}

/**
 * Formats a ZIP / postal code according to the given country's conventions.
 * Currently only Canadian codes get a real transform (space after the first 3
 * chars via `formatCanadianPostalCode`); every other country passes through
 * unchanged, matching what the wallet / on-page inputs already do. Safe to
 * call on empty / nullish input — returned as-is.
 *
 * `countryCode` is the ISO-3166 alpha-2 used elsewhere (`CA`, `US`, ...),
 * compared case-insensitively so wallet-supplied values in either casing work.
 */
export function formatZipCodeByCountry(zipCode: string, countryCode: string): string {
  if (!zipCode) return zipCode;
  return countryCode.toUpperCase() === CountryCode.CANADA ? formatCanadianPostalCode(zipCode) : zipCode;
}

function validateCAZip(value: string): boolean {
  if (!value) {
    return false;
  }

  const regex = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i;
  return regex.test(value);
}

function validateNZZip(value: string): boolean {
  if (!value) {
    return false;
  }
  return /^\d{4}$/.test(value);
}

function validateUKZip(value: string): boolean {
  if (!value) {
    return false;
  }
  return /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/i.test(value);
}

function validateAUZip(value: string): boolean {
  if (!value) {
    return false;
  }
  return /^\d{4}$/.test(value);
}

export function getZipValidator(countryCode: string): (value: string) => boolean {
  switch (countryCode.toUpperCase()) {
    case 'CA':
      return validateCAZip;
    case 'GB':
    case 'UK':
      return validateUKZip;
    case 'NZ':
      return validateNZZip;
    case 'AU':
      return validateAUZip;
    default:
      return validateUSZip;
  }
}

export function validateZipCode(value: string, countryCode: string = 'US'): boolean {
  const validator = getZipValidator(countryCode);
  return validator(value);
}

export function getZipCodeErrorMessageKey(customer: Customer): string {
  const isUSA = isCountryCodeUSA(customer.taxCountry ?? customer.geoLocator?.countryCode2 ?? '');
  return isUSA ? 'fieldErrors.invalidZipCode' : 'fieldErrors.invalidPostalCode';
}
